import React, { useState, useEffect, useCallback, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Search, MapPin, Folder, Briefcase, ExternalLink, TriangleAlert, Plus, Minus } from 'lucide-react';
import './App.css';

// Configuration
const CONFIG = {
  BOARD_TOKEN: 'emergentlabsinc',
  DEPARTMENT_ORDER: [
    'Engineering',
    'Analytics',
    'Product',
    'Design',
    'Founder\'s Office',
    'HR',
    'Growth & Marketing',
    'AI Agent Reliability',
    'Talent Pool',
  ],
  DEPARTMENT_MAPPING: {
    'Product Management': 'Product',
    'Human Resource': 'HR',
    'Marketing/Growth': 'Growth & Marketing',
  },
  GREENHOUSE_URL: 'https://job-boards.greenhouse.io/emergentlabsinc',
  SHOW_EMPTY_DEPARTMENTS: false,
  ENABLE_FILTERS: true,
  CACHE_DURATION: 5 * 60 * 1000, // 5 minutes
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function App() {
  // State
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [locations, setLocations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [expandedDepts, setExpandedDepts] = useState({});
  const [stats, setStats] = useState({ jobs: 0, departments: 0, locations: 0 });

  // Analytics refs
  const sessionId = useRef(uuidv4());
  const sessionStartTime = useRef(Date.now());
  const lastActivityTime = useRef(Date.now());
  const totalActiveTime = useRef(0);
  const isPageVisible = useRef(true);
  const pingInterval = useRef(null);

  // Analytics helper function
  const trackEvent = useCallback(async (eventType, data = {}) => {
    try {
      const eventData = {
        event_type: eventType,
        session_id: sessionId.current,
        referrer: document.referrer || window.location.href,
        ...data,
      };

      // Use sendBeacon for session_end to ensure it's sent even if page is closing
      if (eventType === 'session_end') {
        const blob = new Blob([JSON.stringify(eventData)], { type: 'application/json' });
        navigator.sendBeacon(`${BACKEND_URL}/api/analytics/event`, blob);
      } else {
        // Regular fetch for other events
        await fetch(`${BACKEND_URL}/api/analytics/event`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(eventData),
        });
      }
    } catch (err) {
      console.error('Analytics tracking failed:', err);
    }
  }, []);

  // Track session start
  useEffect(() => {
    trackEvent('session_start');

    // Track page visibility changes
    const handleVisibilityChange = () => {
      if (document.hidden) {
        isPageVisible.current = false;
        // Save current active time when tab becomes hidden
        const now = Date.now();
        totalActiveTime.current += (now - lastActivityTime.current);
      } else {
        isPageVisible.current = true;
        lastActivityTime.current = Date.now();
      }
    };

    // Track user activity (mousemove, keydown, scroll, touchstart)
    const handleActivity = () => {
      if (isPageVisible.current) {
        lastActivityTime.current = Date.now();
      }
    };

    // Send session pings every 15 seconds
    pingInterval.current = setInterval(() => {
      if (isPageVisible.current) {
        const now = Date.now();
        totalActiveTime.current += (now - lastActivityTime.current);
        lastActivityTime.current = now;
      }
      trackEvent('session_ping', { active_ms: totalActiveTime.current });
    }, 15000);

    // Send session end on page unload
    const handleBeforeUnload = () => {
      if (isPageVisible.current) {
        const now = Date.now();
        totalActiveTime.current += (now - lastActivityTime.current);
      }
      trackEvent('session_end', { active_ms: totalActiveTime.current });
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    ['mousemove', 'keydown', 'scroll', 'touchstart'].forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      ['mousemove', 'keydown', 'scroll', 'touchstart'].forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (pingInterval.current) clearInterval(pingInterval.current);
    };
  }, [trackEvent]);

  // Fetch jobs from Greenhouse
  const fetchJobs = useCallback(async () => {
    try {
      // Check cache
      const cacheKey = `greenhouse_jobs_${CONFIG.BOARD_TOKEN}`;
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < CONFIG.CACHE_DURATION) {
          return data;
        }
      }

      // Fetch from Greenhouse
      const response = await fetch(
        `https://boards-api.greenhouse.io/v1/boards/${CONFIG.BOARD_TOKEN}/jobs?content=true`
      );

      if (!response.ok) throw new Error('Failed to fetch jobs');

      const result = await response.json();
      const jobsData = result.jobs || [];

      // Cache the data
      sessionStorage.setItem(
        cacheKey,
        JSON.stringify({ data: jobsData, timestamp: Date.now() })
      );

      return jobsData;
    } catch (err) {
      console.error('Error fetching jobs:', err);
      throw err;
    }
  }, []);

  // Initialize - fetch jobs
  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        const jobsData = await fetchJobs();
        setJobs(jobsData);
        setFilteredJobs(jobsData);

        // Extract unique locations and departments
        const uniqueLocations = [...new Set(jobsData.map(job => job.location.name).filter(Boolean))];
        const uniqueDepartments = [...new Set(
          jobsData.flatMap(job => {
            return job.departments.map(d => {
              const name = d.name.trim();
              return CONFIG.DEPARTMENT_MAPPING[name] || name;
            });
          }).filter(Boolean)
        )];

        setLocations(uniqueLocations.sort());
        setDepartments(uniqueDepartments.sort());

        // Calculate stats
        setStats({
          jobs: jobsData.length,
          departments: uniqueDepartments.length,
          locations: uniqueLocations.length,
        });

        setLoading(false);
      } catch (err) {
        setError(true);
        setLoading(false);
      }
    };

    init();
  }, [fetchJobs]);

  // Filter jobs
  useEffect(() => {
    let filtered = jobs;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(job => {
        const title = job.title?.toLowerCase() || '';
        const location = job.location?.name?.toLowerCase() || '';
        const depts = job.departments?.map(d => d.name.trim().toLowerCase()).join(' ') || '';
        return title.includes(term) || location.includes(term) || depts.includes(term);
      });
    }

    if (selectedLocation) {
      filtered = filtered.filter(job => job.location?.name === selectedLocation);
    }

    if (selectedDepartment) {
      filtered = filtered.filter(job => 
        job.departments?.some(d => d.name.trim() === selectedDepartment)
      );
    }

    setFilteredJobs(filtered);
  }, [searchTerm, selectedLocation, selectedDepartment, jobs]);

  // Group jobs by department
  const groupedJobs = React.useMemo(() => {
    const groups = {};
    filteredJobs.forEach(job => {
      job.departments.forEach(dept => {
        // Trim department name to handle trailing spaces from API
        let deptName = dept.name.trim();
        
        // Map department name if mapping exists
        if (CONFIG.DEPARTMENT_MAPPING[deptName]) {
          deptName = CONFIG.DEPARTMENT_MAPPING[deptName];
        }

        if (!groups[deptName]) {
          groups[deptName] = [];
        }
        groups[deptName].push(job);
      });
    });

    // Sort departments by CONFIG.DEPARTMENT_ORDER
    const sortedDepts = Object.keys(groups).sort((a, b) => {
      const indexA = CONFIG.DEPARTMENT_ORDER.indexOf(a);
      const indexB = CONFIG.DEPARTMENT_ORDER.indexOf(b);
      if (indexA === -1 && indexB === -1) return a.localeCompare(b);
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });

    return sortedDepts.map(dept => ({ name: dept, jobs: groups[dept] }));
  }, [filteredJobs]);

  // Handle apply click
  const handleApplyClick = useCallback((job) => {
    // Track analytics
    trackEvent('apply_click', {
      job_id: job.id?.toString(),
      job_title: job.title,
      department: job.departments?.[0]?.name,
      job_location: job.location?.name,
    });

    // Open job in new tab
    window.open(job.absolute_url, '_blank', 'noopener,noreferrer');
  }, [trackEvent]);

  // Clear filters
  const clearFilters = () => {
    setSearchTerm('');
    setSelectedLocation('');
    setSelectedDepartment('');
  };

  // Toggle department expansion
  const toggleDepartment = (deptName) => {
    setExpandedDepts(prev => ({ ...prev, [deptName]: !prev[deptName] }));
  };

  // Iframe auto-resize
  useEffect(() => {
    const sendHeight = () => {
      if (window.parent !== window) {
        const height = document.documentElement.scrollHeight;
        window.parent.postMessage({ type: 'resize', height }, '*');
      }
    };

    // Send height on content changes
    sendHeight();
    const observer = new ResizeObserver(sendHeight);
    observer.observe(document.body);

    return () => observer.disconnect();
  }, [filteredJobs, loading, error]);

  // Render Loading State
  if (loading) {
    return (
      <div className="job-board" data-testid="job-board">
        {null}
        <div className="loading" data-testid="loading-state">
          <div className="spinner"></div>
          <p>Loading open positions...</p>
          <div className="skeleton-cards">
            {[1, 2, 3].map(i => (
              <div key={i} className="skeleton-card">
                <div className="skeleton-title"></div>
                <div className="skeleton-meta"></div>
                <div className="skeleton-button"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Render Error State
  if (error) {
    return (
      <div className="job-board" data-testid="job-board">
        <div className="error" data-testid="error-state">
          <TriangleAlert className="error-icon" size={48} />
          <h2>Unable to load positions</h2>
          <p>We're having trouble loading our open positions. Please try again or visit our careers page directly.</p>
          <a
            href={CONFIG.GREENHOUSE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="error-link"
            data-testid="error-link"
          >
            View positions on Greenhouse
            <ExternalLink size={16} />
          </a>
        </div>
      </div>
    );
  }

  // Render Empty State
  if (jobs.length === 0) {
    return (
      <div className="job-board" data-testid="job-board">
        {null}
        <div className="empty" data-testid="empty-state">
          <Briefcase className="empty-icon" size={48} />
          <h2>No open positions</h2>
          <p>We don't have any open positions at the moment. Check back soon or follow us for updates.</p>
        </div>
      </div>
    );
  }

  // Render No Results (after filtering)
  if (filteredJobs.length === 0) {
    return (
      <div className="job-board" data-testid="job-board">
        {null}

        {null}

        {CONFIG.ENABLE_FILTERS && (
          <div className="filters" data-testid="filters">
            <div className="filters__search">
              <Search className="filters__search-icon" size={20} />
              <input
                type="text"
                className="filters__search-input"
                placeholder="Search positions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="search-input"
              />
            </div>
            <select
              className="filters__select"
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
              data-testid="location-filter"
            >
              <option value="">All Locations</option>
              {locations.map(loc => (
                <option key={loc} value={loc}>{loc}</option>
              ))}
            </select>
          </div>
        )}

        <div className="no-results" data-testid="no-results">
          <h3>No positions match your search criteria</h3>
          <p>Try adjusting your filters or search term</p>
          <button className="clear-filters-btn" onClick={clearFilters} data-testid="clear-filters">
            Clear filters
          </button>
        </div>
      </div>
    );
  }

  // Render Jobs
  return (
    <div className="job-board" data-testid="job-board">
      {null}

      {null}

      {CONFIG.ENABLE_FILTERS && (
        <div className="filters-container">
          <div className="filters" data-testid="filters">
            <div className="filters__search">
              <Search className="filters__search-icon" size={20} />
              <input
                type="text"
                className="filters__search-input"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="search-input"
              />
            </div>
            <div className="filters__location">
              <select
                className="filters__select"
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
                data-testid="location-filter"
              >
                <option value="">All Locations</option>
                {locations.map(loc => (
                  <option key={loc} value={loc}>{loc}</option>
                ))}
              </select>
            </div>
            <div className="filters__department">
              <select
                className="filters__select"
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
                data-testid="department-filter"
              >
                <option value="">Department</option>
                {departments.map(dept => (
                  <option key={dept} value={dept}>{dept}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="job-count">
            {filteredJobs.length} jobs
          </div>
        </div>
      )}

      <div className="jobs-container" data-testid="jobs-container">
        {groupedJobs.map(({ name, jobs: deptJobs }) => (
          <div key={name} className="department" data-testid={`department-${name}`}>
            <button
              className="department__header"
              onClick={() => toggleDepartment(name)}
              data-testid={`department-toggle-${name}`}
            >
              <h2 className="department__title">
                {name}
              </h2>
              <div className="department__meta">
                <span className="department__count">{deptJobs.length} Roles</span>
                {expandedDepts[name] ? (
                  <Minus className="department__icon" size={20} />
                ) : (
                  <Plus className="department__icon" size={20} />
                )}
              </div>
            </button>

            {expandedDepts[name] && (
              <div className="department__jobs">
                {deptJobs.map(job => (
                    <div key={job.id} className="job-card" data-testid={`job-card-${job.id}`}>
                      <div className="job-card__content">
                        <h3 className="job-card__title">{job.title}</h3>
                        <span className="job-card__location">
                          {job.location.name}
                        </span>
                      </div>
                      <button
                        className="job-card__apply"
                        onClick={() => handleApplyClick(job)}
                        data-testid={`apply-btn-${job.id}`}
                      >
                        Apply
                      </button>
                    </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default App;
